import { useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MergeRequestDTO } from '@clearflow/shared';
import { api } from '../lib/api.ts';
import { pipelineBadgeClass } from '../features/graph/GitGraph.tsx';

interface Props {
  repoId: string;
  mr: MergeRequestDTO;
  /** Bord droit de la sidebar (viewport coords) — la tooltip s'accroche juste à droite, pas au curseur. */
  anchorX: number;
  /** Bornes verticales (viewport coords) de la ligne MR survolée, pour centrer la tooltip dessus. */
  rowTop: number;
  rowBottom: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMerged: () => void;
}

const SIDEBAR_GAP = 5;
const VIEWPORT_MARGIN = 8;
// Rayon de la tooltip (6px) + moitié de la hauteur de la pointe (8px) : la pointe ne doit
// jamais empiéter sur un coin arrondi, sinon elle se détache visuellement du contour.
const ARROW_MARGIN = 14;

// Détail (statut pipeline, conflits, droit de merge) récupéré à la demande au survol —
// contrairement au reste de la tooltip (mr), il n'est pas disponible dans la liste des MR :
// GitLab ne renvoie `pipeline`/`user.can_merge` que sur l'endpoint MR unitaire, car calculer
// le droit de merge par utilisateur pour chaque ligne d'une liste serait trop coûteux.
export function MrTooltip({ repoId, mr, anchorX, rowTop, rowBottom, onMouseEnter, onMouseLeave, onMerged }: Props) {
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(() => Math.max(VIEWPORT_MARGIN, rowTop));
  // Position de la pointe relative au coin haut-gauche de la tooltip — pas juste "50%", car
  // dès que `top` est clampé (ligne près d'un bord du viewport) le centre de la ligne ne
  // coïncide plus avec le centre de la tooltip. Sans ça la pointe dérive du dot qu'elle est
  // censée désigner, façon VS Code (Source Control > Graph).
  const [arrowTop, setArrowTop] = useState(() => (rowTop + rowBottom) / 2 - Math.max(VIEWPORT_MARGIN, rowTop));

  // Centrée verticalement sur la ligne survolée par défaut ; redécalée vers le haut/bas si
  // son contenu (qui varie — pipeline/labels/description/bouton merge se chargent après coup)
  // déborderait du viewport. Le ResizeObserver couvre aussi bien la mesure initiale que toute
  // croissance ultérieure (ex: le bouton "Merger" qui apparaît une fois detailQuery résolue).
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el) return undefined;
    const reposition = () => {
      const height = el.offsetHeight;
      const rowCenterY = (rowTop + rowBottom) / 2;
      const ideal = rowCenterY - height / 2;
      const clampedTop = Math.max(
        VIEWPORT_MARGIN,
        Math.min(ideal, window.innerHeight - height - VIEWPORT_MARGIN),
      );
      setTop(clampedTop);
      setArrowTop(Math.max(ARROW_MARGIN, Math.min(rowCenterY - clampedTop, height - ARROW_MARGIN)));
    };
    reposition();
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rowTop, rowBottom]);

  const detailQuery = useQuery({
    queryKey: ['mr-detail', repoId, mr.iid],
    queryFn: () => api.mergeRequestDetail(repoId, mr.iid),
    staleTime: 15_000,
  });

  const mergeMutation = useMutation({
    mutationFn: () => api.mergeMergeRequest(repoId, mr.iid, detailQuery.data!.sha),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merge-requests', repoId] });
      queryClient.invalidateQueries({ queryKey: ['graph', repoId] });
      onMerged();
    },
  });

  const canMerge = detailQuery.data?.canMerge ?? false;
  // On ne se fie pas qu'à detailedMergeStatus : GitLab a des bugs documentés de désync
  // entre ce champ (parfois stale/en cache) et has_conflicts — voir issues gitlab-org/gitlab
  // #365299 et #386661. On exige les deux pour activer le bouton.
  const isMergeable = detailQuery.data?.detailedMergeStatus === 'mergeable'
    && detailQuery.data?.hasConflicts === false;

  return (
    <div
      ref={tooltipRef}
      className="mr-tooltip"
      style={{ left: anchorX + SIDEBAR_GAP, top, '--arrow-top': `${arrowTop}px` } as React.CSSProperties}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="mr-tooltip-header">
        <code className="mr-tooltip-iid">!{mr.iid}</code>
        <span className="mr-tooltip-title">{mr.title}</span>
        {mr.draft && <span className="badge mr-draft-badge">Draft</span>}
      </div>
      <div className="mr-tooltip-meta">
        <span className="mr-tooltip-branches">{mr.sourceBranch} → {mr.targetBranch}</span>
        <span>{mr.authorName}</span>
      </div>
      {detailQuery.data?.pipelineStatus && (
        <div className="mr-tooltip-pipeline">
          <span className="mr-tooltip-pipeline-label">Pipeline</span>
          <span className={`branch-tooltip-badge ${pipelineBadgeClass(detailQuery.data.pipelineStatus)}`}>
            {detailQuery.data.pipelineStatus}
          </span>
        </div>
      )}
      {mr.labels.length > 0 && (
        <div className="mr-tooltip-labels">
          {mr.labels.map((label) => (
            <span key={label} className="mr-tooltip-label">{label}</span>
          ))}
        </div>
      )}
      {mr.description && <div className="mr-tooltip-description">{mr.description}</div>}
      <div className="mr-tooltip-footer">
        Mis à jour {new Date(mr.updatedAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
      </div>
      {canMerge && (
        <div className="mr-tooltip-actions">
          {mergeMutation.isError ? (
            <span className="badge error">Erreur : {(mergeMutation.error as Error).message}</span>
          ) : confirming ? (
            <button
              className="mr-tooltip-merge-btn confirming"
              disabled={mergeMutation.isPending || !isMergeable}
              onClick={() => mergeMutation.mutate()}
            >
              {mergeMutation.isPending ? 'Merge en cours…' : 'Confirmer le merge ?'}
            </button>
          ) : (
            <button
              className="mr-tooltip-merge-btn"
              disabled={!isMergeable}
              title={isMergeable
                ? undefined
                : `Non mergeable : ${detailQuery.data?.hasConflicts ? 'conflits détectés' : detailQuery.data?.detailedMergeStatus}`}
              onClick={() => setConfirming(true)}
            >
              Merger
            </button>
          )}
        </div>
      )}
    </div>
  );
}
